(function () {
  /* SICLIPS backport (2026-07-26) of Shopify's upstream Horizon fix (~v4.11): cross-document
     View Transitions HANG in Meta's Android in-app browsers (Instagram/Facebook render sites in
     Android System WebView, which starts the transition but never finishes it -> white/frozen
     page on every same-origin navigation; iOS WKWebView is unaffected). When such an engine is
     detected we opt the document OUT of cross-document transitions and release the render
     blocker immediately so first paint can never be held. Re-apply after any base-theme update
     (see CLAUDE.md §3).
     2026-08-01 extension, aligned with upstream Horizon v4.1.3 (commit cd022a365f): the
     in-app-browser token branch is PLATFORM-AGNOSTIC (Shopify's guard covered iOS from day
     one via the FBIOS token in v4.1.1 — WKWebView has supported cross-document view
     transitions AND blocking=render since iOS/Safari 18.2, so iPhone/iPad Instagram+Facebook
     sessions run the same fragile mechanism; iPadOS in-app UAs can even report a
     desktop-class "Macintosh" UA, hence no platform gate on the token branch). The
     transition is purely cosmetic — inside ANY in-app browser we disable it. */
  function shouldDisableCrossDocumentViewTransitions() {
    const ua = navigator.userAgent;
    const inAppBrowser = /FBAN|FBAV|FB_IAB|FBIOS|Instagram|musical_ly|Bytedance(?:Webview)?|trill|TikTok/.test(ua);
    if (inAppBrowser) return true;
    return /\bAndroid\b/.test(ua) && /;\s?wv\)/.test(ua);
  }

  const disableViewTransitions = shouldDisableCrossDocumentViewTransitions();

  if (disableViewTransitions) {
    const style = document.createElement('style');
    style.textContent = '@view-transition { navigation: none; }';
    document.head.appendChild(style);
  }

  const viewTransitionRenderBlocker = document.getElementById('view-transition-render-blocker');
  // Remove the view transition render blocker if the user has reduced motion enabled or is on a low power device.
  if (disableViewTransitions || window.matchMedia('(prefers-reduced-motion: reduce)').matches || isLowPowerDevice()) {
    viewTransitionRenderBlocker?.remove();
  } else {
    // If the browser didn't manage to parse the main content quickly, at least let the user see something.
    // We're aiming for the FCP to be under 1.8 seconds since the navigation started.
    const RENDER_BLOCKER_TIMEOUT_MS = Math.max(0, 1800 - performance.now());

    setTimeout(() => {
      viewTransitionRenderBlocker?.remove();
    }, RENDER_BLOCKER_TIMEOUT_MS);
  }

  const idleCallback = typeof requestIdleCallback === 'function' ? requestIdleCallback : setTimeout;

  window.addEventListener('pageswap', async (event) => {
    const { viewTransition } = /** @type {PageSwapEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    // Cancel view transition on user interaction to improve INP (Interaction to Next Paint)
    ['pointerdown', 'keydown'].forEach((eventName) => {
      document.addEventListener(
        eventName,
        () => {
          viewTransition.skipTransition();
        },
        { once: true }
      );
    });

    // Clean in case you landed on the pdp first. We want to remove the default transition type on the PDP media gallery so there is no duplicate transition name
    document
      .querySelectorAll('[data-view-transition-type]:not([data-view-transition-triggered])')
      .forEach((element) => {
        element.removeAttribute('data-view-transition-type');
      });

    const transitionTriggered = document.querySelector('[data-view-transition-triggered]');
    const transitionType = transitionTriggered?.getAttribute('data-view-transition-type');

    if (transitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(transitionType);
      /* SICLIPS: storage can be denied inside restricted WebViews — a throw here would only
         break transition-type labeling, never the page; swallow it (CLAUDE.md §3) */
      try { sessionStorage.setItem('custom-transition-type', transitionType); } catch (e) {}
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
      try { sessionStorage.removeItem('custom-transition-type'); } catch (e) {}
    }
  });

  window.addEventListener('pagereveal', async (event) => {
    const { viewTransition } = /** @type {PageRevealEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    let customTransitionType = null;
    try { customTransitionType = sessionStorage.getItem('custom-transition-type'); } catch (e) {}

    if (customTransitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(customTransitionType);

      await viewTransition.finished;

      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');

      idleCallback(() => {
        try { sessionStorage.removeItem('custom-transition-type'); } catch (e) {}
        document.querySelectorAll('[data-view-transition-type]').forEach((element) => {
          element.removeAttribute('data-view-transition-type');
        });
      });
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
    }
  });

  /**
   * @param {ViewTransition | null} viewTransition
   * @returns {viewTransition is null}
   */
  function shouldSkipViewTransition(viewTransition) {
    return !(viewTransition instanceof ViewTransition) || isLowPowerDevice() || disableViewTransitions;
  }

  /*
   * We can't import this logic from utilities.js here, but we should keep them in sync.
   */
  function isLowPowerDevice() {
    /* Skip ESLint compatibility check. Number(undefined) <= 2 is always false anyway. */
    /* eslint-disable-next-line compat/compat */
    return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
  }
})();
